/**
 * Small map overlay for a linked event place (modal + /events/:id).
 * Portal + capture-phase Escape so EventSingleModal does not close underneath.
 */

import React, { Suspense, lazy, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { eventPlacePath } from '../../services/eventPlace';
import { fetchPlaceForEventMap } from '../../services/placesApi';
import { hasArchivedFlag } from '../../services/places/placeFlags';

const PlaceMiniMap = lazy(() => import('../Map/PlaceMiniMap'));

/**
 * @param {{ event: object; onClose: () => void }} props
 */
export function EventLocationMapModal({ event, onClose }) {
  const locationPath = eventPlacePath(event);
  const title = String(event?.location || '').trim() || 'Location';
  const [place, setPlace] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const id = String(event?.locationId || '').trim();
    let cancelled = false;
    if (!id) {
      setStatus('missing');
      return undefined;
    }

    fetchPlaceForEventMap(id)
      .then((result) => {
        if (cancelled) return;
        setPlace(result);
        setStatus(result?.hasCoordinates ? 'ready' : 'missing');
      })
      .catch(() => {
        if (cancelled) return;
        setPlace(null);
        setStatus('missing');
      });

    return () => {
      cancelled = true;
    };
  }, [event?.locationId]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  return createPortal(
    <div className="event-location-map-overlay" onClick={onClose} role="presentation">
      <div
        className="event-location-map-panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="event-location-map-head">
          <h2 className="event-location-map-title">{title}</h2>
          <button
            type="button"
            className="event-location-map-close"
            onClick={onClose}
            aria-label="Close map"
          >
            ×
          </button>
        </div>
        <div className="event-location-map-body">
          {status === 'loading' ? (
            <p className="event-location-map-status pub-mono">Loading map…</p>
          ) : null}
          {status === 'ready' ? (
            <Suspense
              fallback={<p className="event-location-map-status pub-mono">Loading map…</p>}
            >
              <PlaceMiniMap place={place} className="event-location-map-canvas" />
            </Suspense>
          ) : null}
          {status === 'missing' ? (
            <p className="event-location-map-status pub-mono">Map unavailable for this place.</p>
          ) : null}
        </div>
        {locationPath && place && !hasArchivedFlag(place) ? (
          <Link
            to={locationPath}
            className="event-location-map-place-link"
            aria-label={`View place: ${title}`}
          >
            View place
          </Link>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

/**
 * Location name in event single: secret / plain text / map-open button.
 *
 * @param {{ event: object; isSecret?: boolean }} props
 */
export function EventLocationControl({ event, isSecret = false }) {
  const [open, setOpen] = useState(false);
  const name = String(event?.location || '').trim();
  const locationPath = eventPlacePath(event);

  if (!name) return null;
  if (isSecret) return 'Secret';
  if (!locationPath) return name;

  return (
    <>
      <button
        type="button"
        className="event-pub-location__link"
        aria-label={`Show map: ${name}`}
        onClick={() => setOpen(true)}
      >
        {name}
      </button>
      {open ? (
        <EventLocationMapModal event={event} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}
